import { mailBusinessSendResultSchema, type MailBusinessSendResult } from '@erp/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  goodsReceiptDetailSchema,
  vendorInvoiceCaptureContextSchema,
  goodsReceiptDraftSchema,
  goodsReceiptWarehouseLookupsSchema,
  goodsReceiptListResponseSchema,
  purchaseOrderDetailSchema,
  purchaseOrderListResponseSchema,
  threeWayMatchResultSchema,
  vendorInvoiceDetailSchema,
  vendorInvoiceOriginalHistorySchema,
  vendorInvoiceCancellationPreviewSchema,
  type VendorInvoiceCancelValues,
  vendorInvoiceListResponseSchema,
  type GoodsReceiptCreateInput,
  type GoodsReceiptDetail,
  type GoodsReceiptDraft,
  type GoodsReceiptListResponse,
  type MatchToleranceValues,
  type PurchaseOrderCancelValues,
  type PurchaseOrderCreateInput,
  type PurchaseOrderDetail,
  type PurchaseOrderEmailValues,
  type PurchaseOrderListResponse,
  type PurchaseOrderStatusUpdateValues,
  type PurchaseOrderUpdateInput,
  type ThreeWayMatchResult,
  type VendorInvoiceApproveValues,
  type VendorInvoiceComplaintValues,
  type VendorInvoiceCreateInput,
  type VendorInvoiceDetail,
  type VendorInvoiceListResponse,
  type VendorInvoiceOriginalUpload,
  type VendorInvoiceUpdateInput,
} from '@erp/shared';
import { apiDownload, apiFetch, apiSend, type ApiError } from '@/lib/api-client';

export interface PurchaseOrderFilters {
  page: number;
  q: string;
  status: string[];
  supplierId: string | null;
}

export interface VendorInvoiceFilters {
  page: number;
  q: string;
  status: string[];
}

export const procurementKeys = {
  all: ['procurement'] as const,
  orders: (filters: PurchaseOrderFilters) => ['procurement', 'orders', filters] as const,
  order: (id: string) => ['procurement', 'order', id] as const,
  receipts: (page: number, q: string) => ['procurement', 'receipts', page, q] as const,
  receipt: (id: string) => ['procurement', 'receipt', id] as const,
  receiptDraft: (orderId: string) => ['procurement', 'receipt-draft', orderId] as const,
  invoices: (filters: VendorInvoiceFilters) => ['procurement', 'invoices', filters] as const,
  invoice: (id: string) => ['procurement', 'invoice', id] as const,
  match: (id: string) => ['procurement', 'match', id] as const,
};

const PAGE_SIZE = 25;

export function useVendorInvoiceCaptureContext(query: string, supplierId: string | null) {
  return useQuery({
    queryKey: ['procurement', 'invoice-capture-context', query.trim(), supplierId],
    queryFn: () =>
      apiFetch(
        `/api/v1/procurement/vendor-invoice-capture-context?${toQuery({
          page: '1',
          pageSize: '8',
          q: query.trim() || undefined,
          supplierId: supplierId ?? undefined,
        })}`,
        vendorInvoiceCaptureContextSchema,
      ),
  });
}

export function useGoodsReceiptWarehouses() {
  return useQuery({
    queryKey: ['procurement', 'receipt-warehouses'],
    queryFn: () =>
      apiFetch('/api/v1/procurement/goods-receipts/warehouses', goodsReceiptWarehouseLookupsSchema),
  });
}

function toQuery(entries: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(entries)) {
    if (value !== undefined && value.length > 0) {
      search.set(key, value);
    }
  }
  return search.toString();
}

// ---------------------------------------------------------------------------
// Bestellungen
// ---------------------------------------------------------------------------

export function usePurchaseOrders(
  filters: PurchaseOrderFilters,
): UseQueryResult<PurchaseOrderListResponse, ApiError> {
  return useQuery({
    queryKey: procurementKeys.orders(filters),
    queryFn: () =>
      apiFetch(
        `/api/v1/procurement/orders?${toQuery({
          page: String(filters.page),
          pageSize: String(PAGE_SIZE),
          q: filters.q.trim(),
          status: filters.status.join(','),
          supplierId: filters.supplierId ?? undefined,
        })}`,
        purchaseOrderListResponseSchema,
      ),
  });
}

export function usePurchaseOrder(
  id: string | undefined,
): UseQueryResult<PurchaseOrderDetail, ApiError> {
  return useQuery({
    queryKey: procurementKeys.order(id ?? ''),
    enabled: id !== undefined,
    queryFn: () => apiFetch(`/api/v1/procurement/orders/${id ?? ''}`, purchaseOrderDetailSchema),
  });
}

export function useCreatePurchaseOrder(): UseMutationResult<
  PurchaseOrderDetail,
  ApiError,
  PurchaseOrderCreateInput
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) =>
      apiFetch('/api/v1/procurement/orders', purchaseOrderDetailSchema, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: procurementKeys.all });
    },
  });
}

export function useUpdatePurchaseOrder(
  id: string,
): UseMutationResult<PurchaseOrderDetail, ApiError, PurchaseOrderUpdateInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) =>
      apiFetch(`/api/v1/procurement/orders/${id}`, purchaseOrderDetailSchema, {
        method: 'PUT',
        body,
      }),
    onSuccess: (order) => {
      queryClient.setQueryData(procurementKeys.order(id), order);
      void queryClient.invalidateQueries({ queryKey: procurementKeys.all });
    },
  });
}

export function usePurchaseOrderStatus(
  id: string,
): UseMutationResult<PurchaseOrderDetail, ApiError, PurchaseOrderStatusUpdateValues> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) =>
      apiFetch(`/api/v1/procurement/orders/${id}/status`, purchaseOrderDetailSchema, {
        method: 'POST',
        body,
      }),
    onSuccess: (order) => {
      queryClient.setQueryData(procurementKeys.order(id), order);
      void queryClient.invalidateQueries({ queryKey: procurementKeys.all });
    },
  });
}

export function useCancelPurchaseOrder(
  id: string,
): UseMutationResult<PurchaseOrderDetail, ApiError, PurchaseOrderCancelValues> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) =>
      apiFetch(`/api/v1/procurement/orders/${id}/cancel`, purchaseOrderDetailSchema, {
        method: 'POST',
        body,
      }),
    onSuccess: (order) => {
      queryClient.setQueryData(procurementKeys.order(id), order);
      void queryClient.invalidateQueries({ queryKey: procurementKeys.all });
    },
  });
}

export function useSendPurchaseOrder(
  id: string,
): UseMutationResult<MailBusinessSendResult, ApiError, PurchaseOrderEmailValues> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) =>
      apiFetch(`/api/v1/procurement/orders/${id}/send-email`, mailBusinessSendResultSchema, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: procurementKeys.all });
    },
  });
}

/** Lädt das Bestell-PDF als Objekt-URL für die Vorschau. */
export function usePurchaseOrderPdf(
  id: string | undefined,
  updatedAt: string | undefined,
): UseQueryResult<string, ApiError> {
  return useQuery({
    queryKey: ['procurement', 'order-pdf', id ?? '', updatedAt ?? ''],
    enabled: id !== undefined && updatedAt !== undefined,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 60_000,
    queryFn: async () => {
      const result = await apiDownload(
        `/api/v1/procurement/orders/${id ?? ''}/pdf`,
        'bestellung.pdf',
      );
      return URL.createObjectURL(result.blob);
    },
  });
}

// ---------------------------------------------------------------------------
// Wareneingang
// ---------------------------------------------------------------------------

export function useGoodsReceipts(
  page: number,
  q: string,
): UseQueryResult<GoodsReceiptListResponse, ApiError> {
  return useQuery({
    queryKey: procurementKeys.receipts(page, q),
    queryFn: () =>
      apiFetch(
        `/api/v1/procurement/goods-receipts?${toQuery({
          page: String(page),
          pageSize: String(PAGE_SIZE),
          q: q.trim(),
        })}`,
        goodsReceiptListResponseSchema,
      ),
  });
}

export function useGoodsReceipt(
  id: string | undefined,
): UseQueryResult<GoodsReceiptDetail, ApiError> {
  return useQuery({
    queryKey: procurementKeys.receipt(id ?? ''),
    enabled: id !== undefined,
    queryFn: () =>
      apiFetch(`/api/v1/procurement/goods-receipts/${id ?? ''}`, goodsReceiptDetailSchema),
  });
}

export function useGoodsReceiptDraft(
  purchaseOrderId: string | null,
): UseQueryResult<GoodsReceiptDraft, ApiError> {
  return useQuery({
    queryKey: procurementKeys.receiptDraft(purchaseOrderId ?? ''),
    enabled: purchaseOrderId !== null && purchaseOrderId.length > 0,
    queryFn: () =>
      apiFetch(
        `/api/v1/procurement/goods-receipts/draft?purchaseOrderId=${purchaseOrderId ?? ''}`,
        goodsReceiptDraftSchema,
      ),
  });
}

export function useCreateGoodsReceipt(): UseMutationResult<
  GoodsReceiptDetail,
  ApiError,
  GoodsReceiptCreateInput
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) =>
      apiFetch('/api/v1/procurement/goods-receipts', goodsReceiptDetailSchema, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: procurementKeys.all });
    },
  });
}

export function usePostGoodsReceipt(): UseMutationResult<GoodsReceiptDetail, ApiError, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) =>
      apiFetch(`/api/v1/procurement/goods-receipts/${id}/post`, goodsReceiptDetailSchema, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: procurementKeys.all });
    },
  });
}

export function useUploadDeliveryNote(): UseMutationResult<
  void,
  ApiError,
  { id: string; file: File }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, file }) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiSend(`/api/v1/procurement/goods-receipts/${id}/delivery-note`, {
        method: 'POST',
        formData,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: procurementKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Eingangsrechnungen
// ---------------------------------------------------------------------------

export function useVendorInvoices(
  filters: VendorInvoiceFilters,
): UseQueryResult<VendorInvoiceListResponse, ApiError> {
  return useQuery({
    queryKey: procurementKeys.invoices(filters),
    queryFn: () =>
      apiFetch(
        `/api/v1/procurement/vendor-invoices?${toQuery({
          page: String(filters.page),
          pageSize: String(PAGE_SIZE),
          q: filters.q.trim(),
          status: filters.status.join(','),
        })}`,
        vendorInvoiceListResponseSchema,
      ),
  });
}

export function useVendorInvoice(
  id: string | undefined,
): UseQueryResult<VendorInvoiceDetail, ApiError> {
  return useQuery({
    queryKey: procurementKeys.invoice(id ?? ''),
    enabled: id !== undefined,
    queryFn: () =>
      apiFetch(`/api/v1/procurement/vendor-invoices/${id ?? ''}`, vendorInvoiceDetailSchema),
  });
}

export function useCreateVendorInvoice(): UseMutationResult<
  VendorInvoiceDetail,
  ApiError,
  VendorInvoiceCreateInput
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) =>
      apiFetch('/api/v1/procurement/vendor-invoices', vendorInvoiceDetailSchema, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: procurementKeys.all });
    },
  });
}

export function useUpdateVendorInvoice(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: VendorInvoiceUpdateInput) =>
      apiFetch(`/api/v1/procurement/vendor-invoices/${id}`, vendorInvoiceDetailSchema, {
        method: 'PUT',
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: procurementKeys.all });
    },
  });
}

export function useUploadInvoiceDocument(): UseMutationResult<
  void,
  ApiError,
  VendorInvoiceOriginalUpload & { id: string; file: File; idempotencyKey: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version, file, reason, idempotencyKey }) => {
      const formData = new FormData();
      formData.append('file', file);
      const params = new URLSearchParams({
        version: String(version),
        ...(reason ? { reason } : {}),
      });
      return apiSend(`/api/v1/procurement/vendor-invoices/${id}/document?${params.toString()}`, {
        method: 'POST',
        formData,
        headers: { 'Idempotency-Key': idempotencyKey },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: procurementKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['vendor-invoice-accounting'] });
    },
  });
}
export function useInvoiceOriginalHistory(
  id: string,
  page = 1,
  q = '',
  sort: 'revision:asc' | 'revision:desc' = 'revision:desc',
) {
  return useQuery({
    queryKey: ['procurement', 'invoice-originals', id, page, q, sort],
    queryFn: () =>
      apiFetch(
        `/api/v1/procurement/vendor-invoices/${id}/originals?${new URLSearchParams({ page: String(page), pageSize: '25', sort, ...(q.trim() ? { q: q.trim() } : {}) }).toString()}`,
        vendorInvoiceOriginalHistorySchema,
      ),
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: (previous) => previous,
  });
}

/** Version identity keeps a replaced source from sharing the previously displayed file cache. */
export function useInvoiceDocument(
  id: string | undefined,
  hasDocument: boolean,
  originalId: string | null = null,
  invoiceVersion = 0,
): UseQueryResult<Blob, ApiError> {
  return useQuery({
    queryKey: ['procurement', 'invoice-document', id ?? '', originalId, invoiceVersion],
    enabled: id !== undefined && hasDocument,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 60_000,
    queryFn: async () => {
      const suffix = originalId ? `/originals/${originalId}/file` : '/document';
      const result = await apiDownload(
        `/api/v1/procurement/vendor-invoices/${id ?? ''}${suffix}`,
        'invoice-original',
      );
      return result.blob;
    },
  });
}

export function useMatchPreview(
  id: string | undefined,
): UseQueryResult<ThreeWayMatchResult, ApiError> {
  return useQuery({
    queryKey: procurementKeys.match(id ?? ''),
    enabled: id !== undefined,
    queryFn: () =>
      apiFetch(`/api/v1/procurement/vendor-invoices/${id ?? ''}/match`, threeWayMatchResultSchema),
  });
}

export function useRunMatch(
  id: string,
): UseMutationResult<ThreeWayMatchResult, ApiError, MatchToleranceValues | undefined> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tolerances) =>
      apiFetch(`/api/v1/procurement/vendor-invoices/${id}/match`, threeWayMatchResultSchema, {
        method: 'POST',
        body: tolerances === undefined ? {} : { tolerances },
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(procurementKeys.match(id), result);
      void queryClient.invalidateQueries({ queryKey: procurementKeys.all });
    },
  });
}

export function useOverrideMatch(
  id: string,
): UseMutationResult<ThreeWayMatchResult, ApiError, { overrideReason: string }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) =>
      apiFetch(
        `/api/v1/procurement/vendor-invoices/${id}/match/override`,
        threeWayMatchResultSchema,
        { method: 'POST', body },
      ),
    onSuccess: (result) => {
      queryClient.setQueryData(procurementKeys.match(id), result);
      void queryClient.invalidateQueries({ queryKey: procurementKeys.all });
    },
  });
}

export function useApproveVendorInvoice(
  id: string,
): UseMutationResult<VendorInvoiceDetail, ApiError, VendorInvoiceApproveValues> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) =>
      apiFetch(`/api/v1/procurement/vendor-invoices/${id}/approve`, vendorInvoiceDetailSchema, {
        method: 'POST',
        body,
      }),
    onSuccess: (invoice) => {
      queryClient.setQueryData(procurementKeys.invoice(id), invoice);
      void queryClient.invalidateQueries({ queryKey: procurementKeys.all });
    },
  });
}

export function useSendComplaint(
  id: string,
): UseMutationResult<MailBusinessSendResult, ApiError, VendorInvoiceComplaintValues> {
  return useMutation({
    mutationFn: (body) =>
      apiFetch(
        `/api/v1/procurement/vendor-invoices/${id}/complaint`,
        mailBusinessSendResultSchema,
        { method: 'POST', body },
      ),
  });
}

export function useVendorInvoiceCancellationPreview(id: string) {
  return useQuery({
    queryKey: [...procurementKeys.invoice(id), 'cancellation-preview'],
    queryFn: () =>
      apiFetch(
        `/api/v1/procurement/vendor-invoices/${id}/cancellation-preview`,
        vendorInvoiceCancellationPreviewSchema,
      ),
  });
}
export function useCancelVendorInvoice(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: VendorInvoiceCancelValues) =>
      apiFetch(`/api/v1/procurement/vendor-invoices/${id}/cancel`, vendorInvoiceDetailSchema, {
        method: 'POST',
        body,
      }),
    onSuccess: async () => {
      await Promise.all(
        [procurementKeys.all, ['financial-payments'], ['ledger'], ['banking']].map((queryKey) =>
          client.invalidateQueries({ queryKey }),
        ),
      );
    },
  });
}
